import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, BACKEND, errMsg } from "@/lib/api";
import { LAYOUT_LABEL } from "@/lib/designer";
import { useSeo } from "@/lib/site";

const STATUS = { pending: "Pendiente", processing: "Procesando", ready: "Listo", error: "Error" };
const fmt = (iso) => new Date(iso).toLocaleString("es-CO", { timeZone: "America/Bogota", dateStyle: "medium", timeStyle: "short" });
const dl = "rounded-full border border-[#080808]/15 px-3 py-1.5 text-xs hover:border-[#080808] transition-colors";

const Row = ({ d, onRetry }) => {
  const s = d.snapshot;
  const r = d.render || {};
  const file = (kind) => `${BACKEND}/api/admin/designs/${d.id}/file?kind=${kind}`;
  const low = (s.quality || []).filter((q) => q.level === "low").length;
  return (
    <li data-testid={`admin-design-${d.id}`} className="grid gap-4 border-b border-[#EAE7E2] py-5 md:grid-cols-[1fr_auto] md:items-center">
      <div>
        <p className="text-sm font-medium">{s.product.size} · {s.width_cm} × {s.height_cm} cm · {LAYOUT_LABEL[s.layout]} · rev. {d.revision}</p>
        <p className="mt-1 text-xs text-[#080808]/60">Confirmado {fmt(d.confirmed_at)} · cliente {d.guest}… · {r.width_px ? `${r.width_px} × ${r.height_px} px · ${r.dpi} ppp` : ""}</p>
        {low > 0 && <p className="mt-1 text-xs text-[#8B1E1E]">{low} foto(s) con baja resolución aceptada(s) por el cliente</p>}
        {r.error && <p className="mt-1 text-xs text-[#8B1E1E]">{r.error}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid={`admin-design-${d.id}-status`} className="mr-2 text-xs uppercase tracking-[0.12em]">{STATUS[r.status] || "—"}</span>
        {r.status === "ready" && <>
          <a className={dl} href={file("preview")} data-testid={`admin-design-${d.id}-preview`}>Vista previa</a>
          <a className={dl} href={file("normal")} data-testid={`admin-design-${d.id}-png`}>PNG</a>
          <a className={dl} href={file("mirror")} data-testid={`admin-design-${d.id}-mirror`}>PNG espejo</a>
        </>}
        {s.cells.map((_, i) => <a key={i} className={dl} href={`${BACKEND}/api/admin/designs/${d.id}/originals/${i}`}>Original {i + 1}</a>)}
        {r.status === "error" && <button className={dl} onClick={() => onRetry(d.id)} data-testid={`admin-design-${d.id}-retry`}>Reintentar</button>}
      </div>
    </li>
  );
};

export default function DesignsAdmin() {
  useSeo({ title: "Diseños · Administración", noindex: true });
  const { data = [], refetch } = useQuery({ queryKey: ["admin-designs"], queryFn: () => api.get("/admin/designs").then((r) => r.data), refetchInterval: 5000 });
  const retry = async (id) => { try { await api.post(`/admin/designs/${id}/retry`); refetch(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="min-h-screen bg-[#FAF8F4] px-5 sm:px-10 py-10">
      <div className="flex items-center justify-between">
        <h1 className="font-serif-display text-4xl">Diseños confirmados</h1>
        <Link to="/studio" className="text-xs uppercase tracking-[0.2em] text-[#080808]/70 hover:text-[#080808]">← Estudio</Link>
      </div>
      <p className="mt-3 text-sm text-[#080808]/65">Archivos de producción PNG a 300 ppp, sRGB, de borde a borde. Aún no hay pedidos pagados: estos diseños no están listos para producción hasta que exista un pago verificado.</p>
      {data.length === 0 ? <p data-testid="admin-designs-empty" className="mt-10 text-sm">Todavía no hay diseños confirmados.</p>
        : <ul className="mt-6">{data.map((d) => <Row key={d.id} d={d} onRetry={retry} />)}</ul>}
    </div>
  );
}
